"use client"

import { useState, useEffect } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Grid,
  Paper,
  Tabs,
  Tab,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  alpha,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Line,
  Area,
  AreaChart,
} from "recharts"

// Componente para el panel de pestañas
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chart-tabpanel-${index}`}
      aria-labelledby={`chart-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index) {
  return {
    id: `chart-tab-${index}`,
    "aria-controls": `chart-tabpanel-${index}`,
  }
}

const GraficasCuentaCorrienteModal = ({ open, handleClose, data }) => {
  const theme = useTheme()
  const [tabValue, setTabValue] = useState(0)
  const [periodoSeleccionado, setPeriodoSeleccionado] = useState("todos")
  const [datosGraficas, setDatosGraficas] = useState({
    porCliente: [],
    porTipoEntidad: [],
    porMes: [],
    porSaldo: [],
    itemsPopulares: [],
  })

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handlePeriodoChange = (event) => {
    setPeriodoSeleccionado(event.target.value)
  }

  // Procesar datos para las gráficas
  useEffect(() => {
    if (!data || !Array.isArray(data)) return

    // Filtrar por periodo si es necesario
    let datosFiltrados = [...data]
    const hoy = new Date()

    if (periodoSeleccionado === "mes") {
      const unMesAtras = new Date()
      unMesAtras.setMonth(unMesAtras.getMonth() - 1)
      datosFiltrados = datosFiltrados.filter((item) => new Date(item.fechaCreacionCC) >= unMesAtras)
    } else if (periodoSeleccionado === "trimestre") {
      const tresMesesAtras = new Date()
      tresMesesAtras.setMonth(tresMesesAtras.getMonth() - 3)
      datosFiltrados = datosFiltrados.filter((item) => new Date(item.fechaCreacionCC) >= tresMesesAtras)
    } else if (periodoSeleccionado === "semestre") {
      const seisMesesAtras = new Date()
      seisMesesAtras.setMonth(seisMesesAtras.getMonth() - 6)
      datosFiltrados = datosFiltrados.filter((item) => new Date(item.fechaCreacionCC) >= seisMesesAtras)
    }

    // Datos por cliente (top 10)
    const clientesMap = new Map()
    datosFiltrados.forEach((item) => {
      const clienteKey = `${item.descripcion} ${item.apellido || ""}`.trim()
      if (!clientesMap.has(clienteKey)) {
        clientesMap.set(clienteKey, {
          saldo: item.saldo || 0,
          transacciones: item.transCount || 0,
          montoPagado: item.montoPagado || 0,
        })
      }
    })

    const porCliente = Array.from(clientesMap.entries())
      .map(([name, data]) => ({
        name,
        saldo: data.saldo,
        transacciones: data.transacciones,
        montoPagado: data.montoPagado,
      }))
      .sort((a, b) => Math.abs(b.saldo) - Math.abs(a.saldo))
      .slice(0, 10)

    // Datos por tipo de entidad
    const tipoEntidadMap = new Map()
    datosFiltrados.forEach((item) => {
      const tipo = item.tipoEntidad || "Sin tipo"
      if (!tipoEntidadMap.has(tipo)) {
        tipoEntidadMap.set(tipo, {
          count: 0,
          saldoTotal: 0,
        })
      }
      tipoEntidadMap.get(tipo).count++
      tipoEntidadMap.get(tipo).saldoTotal += item.saldo || 0
    })

    const porTipoEntidad = Array.from(tipoEntidadMap.entries()).map(([name, data]) => ({
      name,
      count: data.count,
      saldoTotal: data.saldoTotal,
    }))

    // Datos por mes
    const mesesMap = new Map()
    datosFiltrados.forEach((item) => {
      if (item.transacciones && Array.isArray(item.transacciones)) {
        item.transacciones.forEach((tx) => {
          if (tx.fecha) {
            const fecha = new Date(tx.fecha)
            const mesKey = `${fecha.getMonth() + 1}/${fecha.getFullYear()}`

            if (!mesesMap.has(mesKey)) {
              mesesMap.set(mesKey, {
                compras: 0,
                pagos: 0,
              })
            }

            if (tx.idTipoTransaccion === 1) {
              // Compra
              mesesMap.get(mesKey).compras += tx.montoTotal || 0
            } else if (tx.idTipoTransaccion === 2) {
              // Pago
              mesesMap.get(mesKey).pagos += tx.montoTotal || 0
            }
          }
        })
      }
    })

    const porMes = Array.from(mesesMap.entries())
      .map(([month, data]) => ({
        month,
        compras: data.compras,
        pagos: data.pagos,
        balance: data.pagos - data.compras,
      }))
      .sort((a, b) => {
        const [monthA, yearA] = a.month.split("/")
        const [monthB, yearB] = b.month.split("/")
        return new Date(yearA, monthA - 1) - new Date(yearB, monthB - 1)
      })

    // Datos por saldo
    const saldoRanges = [
      { range: "< -100000", count: 0, color: theme.palette.error.dark },
      { range: "-100000 a -50000", count: 0, color: theme.palette.error.main },
      { range: "-50000 a -10000", count: 0, color: theme.palette.error.light },
      { range: "-10000 a 0", count: 0, color: alpha(theme.palette.error.light, 0.7) },
      { range: "0", count: 0, color: theme.palette.success.main },
      { range: "0 a 10000", count: 0, color: alpha(theme.palette.warning.light, 0.7) },
      { range: "10000 a 50000", count: 0, color: theme.palette.warning.light },
      { range: "50000 a 100000", count: 0, color: theme.palette.warning.main },
      { range: "> 100000", count: 0, color: theme.palette.warning.dark },
    ]

    datosFiltrados.forEach((item) => {
      const saldo = item.saldo || 0

      if (saldo < -100000) saldoRanges[0].count++
      else if (saldo >= -100000 && saldo < -50000) saldoRanges[1].count++
      else if (saldo >= -50000 && saldo < -10000) saldoRanges[2].count++
      else if (saldo >= -10000 && saldo < 0) saldoRanges[3].count++
      else if (saldo === 0) saldoRanges[4].count++
      else if (saldo > 0 && saldo <= 10000) saldoRanges[5].count++
      else if (saldo > 10000 && saldo <= 50000) saldoRanges[6].count++
      else if (saldo > 50000 && saldo <= 100000) saldoRanges[7].count++
      else if (saldo > 100000) saldoRanges[8].count++
    })

    const porSaldo = saldoRanges.filter((range) => range.count > 0)

    // Datos de items populares
    const itemsMap = new Map()
    datosFiltrados.forEach((item) => {
      if (item.items && Array.isArray(item.items)) {
        item.items.forEach((producto) => {
          const itemKey = producto.descripcion || "Sin descripción"

          if (!itemsMap.has(itemKey)) {
            itemsMap.set(itemKey, {
              cantidad: 0,
              montoTotal: 0,
            })
          }

          itemsMap.get(itemKey).cantidad += producto.cantidad || 0
          itemsMap.get(itemKey).montoTotal += producto.montoTotal || 0
        })
      }
    })

    const itemsPopulares = Array.from(itemsMap.entries())
      .map(([name, data]) => ({
        name,
        cantidad: data.cantidad,
        montoTotal: data.montoTotal,
      }))
      .sort((a, b) => b.montoTotal - a.montoTotal)
      .slice(0, 10)

    setDatosGraficas({
      porCliente,
      porTipoEntidad,
      porMes,
      porSaldo,
      itemsPopulares,
    })
  }, [data, periodoSeleccionado, theme])

  // Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      maximumFractionDigits: 0,
    }).format(value)
  }

  // Colores para gráficos
  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.success.main,
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#ff7300",
  ]

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, bgcolor: theme.palette.primary.main, color: "white" }}>
        Análisis de Cuentas Corrientes{" "}
        {data && Array.isArray(data) && (
          <Typography variant="subtitle1" component="span" sx={{ ml: 1, fontWeight: "normal" }}>
            ({data.length} cuentas)
          </Typography>
        )}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h6">Visualización de datos</Typography>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="periodo-select-label">Periodo</InputLabel>
            <Select
              labelId="periodo-select-label"
              value={periodoSeleccionado}
              label="Periodo"
              onChange={handlePeriodoChange}
              size="small"
            >
              <MenuItem value="todos">Todos los datos</MenuItem>
              <MenuItem value="mes">Último mes</MenuItem>
              <MenuItem value="trimestre">Último trimestre</MenuItem>
              <MenuItem value="semestre">Último semestre</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="chart tabs" centered>
            <Tab label="Distribución por Saldo" {...a11yProps(0)} />
            <Tab label="Evolución Mensual" {...a11yProps(1)} />
            <Tab label="Top Clientes" {...a11yProps(2)} />
            <Tab label="Productos Populares" {...a11yProps(3)} />
            <Tab label="Tipos de Entidad" {...a11yProps(4)} />
          </Tabs>
        </Box>

        {/* Distribución por Saldo */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>
            Distribución de Cuentas por Rango de Saldo
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 2, height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={datosGraficas.porSaldo}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 60,
                    }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="range" type="category" width={120} />
                    <RechartsTooltip formatter={(value) => [`${value} cuentas`, ""]} />
                    <Bar dataKey="count" name="Cantidad de Cuentas">
                      {datosGraficas.porSaldo.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2, height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={datosGraficas.porSaldo}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="count"
                      nameKey="range"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {datosGraficas.porSaldo.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value, name) => [`${value} cuentas`, name]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Evolución Mensual */}
        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Evolución Mensual de Compras y Pagos
          </Typography>
          <Paper sx={{ p: 2, height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={datosGraficas.porMes}
                margin={{
                  top: 20,
                  right: 30,
                  left: 20,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={(value) => formatCurrency(value)} />
                <RechartsTooltip formatter={(value) => [formatCurrency(value), ""]} />
                <Legend />
                <defs>
                  <linearGradient id="colorCompras" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.palette.warning.main} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={theme.palette.warning.main} stopOpacity={0.1} />
                  </linearGradient>
                  <linearGradient id="colorPagos" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={theme.palette.success.main} stopOpacity={0.8} />
                    <stop offset="95%" stopColor={theme.palette.success.main} stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="compras"
                  stroke={theme.palette.warning.main}
                  fillOpacity={1}
                  fill="url(#colorCompras)"
                  name="Compras"
                />
                <Area
                  type="monotone"
                  dataKey="pagos"
                  stroke={theme.palette.success.main}
                  fillOpacity={1}
                  fill="url(#colorPagos)"
                  name="Pagos"
                />
                <Line
                  type="monotone"
                  dataKey="balance"
                  stroke={theme.palette.info.main}
                  name="Balance"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </TabPanel>

        {/* Top Clientes */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>
            Top 10 Clientes por Saldo
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 2, height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={datosGraficas.porCliente}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 60,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="name"
                      angle={-45}
                      textAnchor="end"
                      height={70}
                      tick={{ fontSize: 12 }}
                      interval={0}
                    />
                    <YAxis tickFormatter={(value) => formatCurrency(value)} />
                    <RechartsTooltip formatter={(value) => [formatCurrency(value), ""]} />
                    <Bar dataKey="saldo" name="Saldo" fill={theme.palette.primary.main} radius={[4, 4, 0, 0]}>
                      {datosGraficas.porCliente.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={entry.saldo < 0 ? theme.palette.error.main : theme.palette.warning.main}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2, height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={datosGraficas.porCliente}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="montoPagado"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {datosGraficas.porCliente.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => [formatCurrency(value), "Monto Pagado"]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Productos Populares */}
        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" gutterBottom>
            Productos Más Populares
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 2, height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={datosGraficas.itemsPopulares}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 60,
                    }}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(value) => formatCurrency(value)} />
                    <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                    <RechartsTooltip formatter={(value) => [formatCurrency(value), "Monto Total"]} />
                    <Bar
                      dataKey="montoTotal"
                      name="Monto Total"
                      fill={theme.palette.secondary.main}
                      radius={[0, 4, 4, 0]}
                    >
                      {datosGraficas.itemsPopulares.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2, height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={datosGraficas.itemsPopulares}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="cantidad"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {datosGraficas.itemsPopulares.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => [`${value} unidades`, "Cantidad"]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tipos de Entidad */}
        <TabPanel value={tabValue} index={4}>
          <Typography variant="h6" gutterBottom>
            Distribución por Tipo de Entidad
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Paper sx={{ p: 2, height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={datosGraficas.porTipoEntidad}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 20,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" orientation="left" />
                    <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => formatCurrency(value)} />
                    <RechartsTooltip />
                    <Legend />
                    <Bar yAxisId="left" dataKey="count" name="Cantidad" fill={theme.palette.info.main} />
                    <Bar yAxisId="right" dataKey="saldoTotal" name="Saldo Total" fill={theme.palette.secondary.main} />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 2, height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={datosGraficas.porTipoEntidad}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="count"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {datosGraficas.porTipoEntidad.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value, name) => [`${value} cuentas`, name]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>
    </Dialog>
  )
}

export default GraficasCuentaCorrienteModal
